import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

// Deterministic colour wash from a name -> one of 3 muted tints. Keeps the
// avatar stack readable without introducing new brand colours.
const avatarTint = (name) => {
  const tints = [
    "bg-foreground/10 text-foreground",
    "bg-foreground/[0.07] text-foreground/80",
    "bg-foreground/[0.05] text-foreground/70",
  ];
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  return tints[Math.abs(hash) % tints.length];
};

/**
 * Connected-by-your-team strip, ported from the Lovable Your Tools page. Sits
 * above the tool grid as a social-proof shortcut: tapping a pill seeds the
 * search and surfaces that tool's card.
 *
 * Lovable's version says "most used ... last 30 days" from a hand-curated map.
 * We have no usage log, so the strip is built from who actually connected each
 * tool on this workspace and is labelled accordingly.
 */
const TeamUsageStrip = ({ tools, onSelect }) => {
  const visible = useMemo(() => (tools || []).slice(0, 6), [tools]);
  if (visible.length === 0) return null;

  return (
    <section
      aria-label="Connected by your team"
      className="mb-4 rounded-2xl border border-border/60 bg-muted/30 px-4 py-2.5 sm:px-5"
    >
      <div className="flex items-center gap-3">
        <div className="hidden shrink-0 flex-col border-r border-border/60 pr-3 lg:flex">
          <span className="page-eyebrow leading-tight">Connected by your team</span>
          <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground/70">
            Across your workspace
          </span>
        </div>
        <span className="page-eyebrow shrink-0 lg:hidden">Connected</span>
        <div className="no-scrollbar -mx-4 flex flex-1 snap-x snap-mandatory gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          {visible.map((tool) => {
            const stack = (tool.teammates || []).slice(0, 4);
            const overflow = Math.max(
              0,
              (tool.teammates || []).length - stack.length,
            );
            return (
              <button
                key={tool.name}
                type="button"
                onClick={() => onSelect?.(tool.searchTerm || tool.name)}
                title={tool.name}
                className="interactive-surface group flex shrink-0 snap-start items-center gap-2.5 whitespace-nowrap rounded-full border border-border/70 bg-background px-3 py-1.5 text-[12px] hover:border-foreground/40"
              >
                {tool.logoUrl && (
                  <img
                    src={tool.logoUrl}
                    alt=""
                    className="h-4 w-4 shrink-0 object-contain"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <span className="max-w-[160px] truncate font-medium leading-none text-foreground">
                  {tool.name}
                </span>
                <span aria-hidden className="leading-none text-muted-foreground/40">
                  *
                </span>
                <span className="flex -space-x-1.5">
                  <TooltipProvider delayDuration={150}>
                    {stack.map((name) => (
                      <Tooltip key={name}>
                        <TooltipTrigger asChild>
                          <span
                            className={`grid h-5 w-5 place-items-center rounded-full border border-background text-[9px] font-medium capitalize leading-none ${avatarTint(
                              name,
                            )}`}
                          >
                            {name.charAt(0)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="capitalize">
                          {name}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                  {overflow > 0 && (
                    <span className="grid h-5 w-5 place-items-center rounded-full border border-background bg-muted text-[9px] font-medium leading-none text-muted-foreground">
                      +{overflow}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TeamUsageStrip;
